var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { UserSettings } from "../models/user-settings";
import { Collection } from "../models/collection";
import * as localForage from "localforage";
import { SyncService } from "./sync.service";
import { AmplifyService } from 'aws-amplify-angular';
import { ExpiringValue } from '@sailplane/expiring-value/dist/expiring-value';
import { StorageLocation } from '../models/storage-location';
const CLOUD_LIST_PERIOD = 10000;
export class PersistenceException {
    constructor(statusCode, statusText, error) {
        this.statusCode = statusCode;
        this.statusText = statusText;
        this.error = error;
    }
    toString() {
        return `PersistenceException code #${this.statusCode}: ${this.statusText}`;
    }
}
export class PersistenceService {
    constructor(syncSvc, amplifySvc) {
        this.syncSvc = syncSvc;
        this.amplifySvc = amplifySvc;
        this.isLocalUser = false;
        this.usersLatestFiles = new ExpiringValue(() => this.refreshLatestFiles(), CLOUD_LIST_PERIOD);
        this.cloud = this.amplifySvc.storage();
        localForage.config({
            name: 'SqAC',
            size: 20000000,
            storeName: 'SqAC',
            version: 2,
            description: 'Stores your login session and data for editing and offline use.'
        });
    }
    signOut() {
        this.syncSvc.reset();
        localForage.clear().then();
        return this.amplifySvc.auth().signOut();
    }
    isNewerInCloud(model) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isLocalUser) {
                return false;
            }
            else {
                const latestCloud = (yield this.usersLatestFiles.get()).get(model.id);
                return (latestCloud && latestCloud.revision > model.revision);
            }
        });
    }
    loadUser(isLocalAccount = false) {
        return __awaiter(this, void 0, void 0, function* () {
            this.isLocalUser = isLocalAccount;
            try {
                let json = (yield localForage.getItem('settings'));
                if (isLocalAccount) {
                    if (!json) {
                        throw new PersistenceException(404, "No such user");
                    }
                }
                else if (this.syncSvc.isOnline()) {
                    const settingsLocation = (yield this.usersLatestFiles.get()).get('settings');
                    if (!settingsLocation) {
                        throw new PersistenceException(404, "No such user");
                    }
                    if (settingsLocation && (!json || settingsLocation.revision > json.revision)) {
                        const downloadedObj = yield this.cloud.get(settingsLocation.key, settingsLocation.toStorageConfig(true));
                        try {
                            const downloadedStr = downloadedObj.Body.toString('utf-8');
                            const downloadedJson = JSON.parse(downloadedStr);
                            if (!json || downloadedJson.revision > json.revision) {
                                json = downloadedJson;
                                json.isCloudBacked = true;
                                localForage.setItem(settingsLocation.path, json).then();
                                console.log("Loaded settings", JSON.stringify(json));
                            }
                            else {
                                console.debug("No change in user settings");
                            }
                        }
                        catch (badCloudUpdate) {
                            console.error("Fetch of updated settings from cloud failure", badCloudUpdate);
                        }
                    }
                }
                if (!json) {
                    throw new PersistenceException(0, "Offline");
                }
                const user = UserSettings.fromJSON(json);
                user.isAvailable = true;
                this.syncSvc.reset();
                return user;
            }
            catch (error) {
                throw this.translateError(error);
            }
        });
    }
    cloudSaveUser(userSettings) {
        return this.saveModelToCloud(userSettings, new StorageLocation('settings'));
    }
    localStoreUser(userSettings) {
        return this.saveModelToLocal(userSettings, new StorageLocation('settings'));
    }
    loadUserCollectionsFromCloud() {
        return __awaiter(this, void 0, void 0, function* () {
            const allPrivateCollections = Array.from((yield this.usersLatestFiles.get()).values()).filter(location => location.id !== 'settings');
            return Promise.all(allPrivateCollections.map(location => this.cloudLoadCollection(location)));
        });
    }
    loadCollection(collectionModelOrPath) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const location = new StorageLocation(collectionModelOrPath);
                let model = Collection.fromJSON((yield localForage.getItem(location.id)));
                if (this.syncSvc.isOnline() && (!model || (yield this.isNewerInCloud(model)))) {
                    model = yield this.cloudLoadCollection(location);
                }
                if (!model) {
                    throw new PersistenceException(0, "Unable to load collection " + location.id);
                }
                return model;
            }
            catch (error) {
                throw this.translateError(error);
            }
        });
    }
    cloudLoadCollection(location) {
        return __awaiter(this, void 0, void 0, function* () {
            console.debug("cloudLoadCollection", location);
            const downloadedObj = yield this.cloud.get(location.key, location.toStorageConfig(true));
            try {
                const downloadedStr = downloadedObj.Body.toString('utf-8');
                const json = JSON.parse(downloadedStr);
                json.isCloudBacked = true;
                localForage.setItem(location.id, json).then();
                return Collection.fromJSON(json);
            }
            catch (badCloudUpdate) {
                console.error("Fetch of updated collection from cloud failure", badCloudUpdate);
            }
        });
    }
    cloudReloadCollection(collectionPath) {
        return __awaiter(this, void 0, void 0, function* () {
            yield localForage.removeItem(new StorageLocation(collectionPath).id);
            return this.loadCollection(collectionPath);
        });
    }
    cloudSaveCollection(collection) {
        return (this.isLocalUser)
            ? Promise.resolve(collection)
            : this.saveModelToCloud(collection, new StorageLocation(collection, true));
    }
    localStoreCollection(collection) {
        return this.saveModelToLocal(collection, new StorageLocation(collection, true));
    }
    loadHistory(collection) {
        return __awaiter(this, void 0, void 0, function* () {
            const list = yield this.cloudList(new StorageLocation(collection.id, true));
            const results = [];
            for (const location of list) {
                const downloadedObj = yield this.cloud.get(location.key, location.toStorageConfig(true));
                try {
                    const downloadedStr = downloadedObj.Body.toString('utf-8');
                    const downloadedJson = JSON.parse(downloadedStr);
                    results.push(Collection.fromJSON(downloadedJson));
                }
                catch (badCloudUpdate) {
                    console.error("Fetch of collection from cloud failure", badCloudUpdate);
                }
            }
            return results.sort((a, b) => a.revision - b.revision);
        });
    }
    cloudList(location) {
        return __awaiter(this, void 0, void 0, function* () {
            const isPrivate = location.level === 'private';
            try {
                const results = [];
                if (this.syncSvc.isOnline()) {
                    const contents = yield this.cloud.list(location.key, location.toStorageConfig());
                    console.log("cloudList data", contents);
                    for (const item of contents) {
                        const path = isPrivate
                            ? item.key
                            : item.owner.id + '/' + item.key;
                        results.push(new StorageLocation(path, isPrivate));
                    }
                }
                console.debug("Files at", location.path, ':', results);
                return results;
            }
            catch (error) {
                throw this.translateError(error);
            }
        });
    }
    saveModelToLocal(model, location) {
        return __awaiter(this, void 0, void 0, function* () {
            if (model.isDirty) {
                model.modified = new Date();
                model.isCloudBacked = false;
                model.isDirty = false;
                const json = model.toJSON();
                localForage.setItem(location.id, json).then();
                console.log("Local stored " + location.path);
                this.usersLatestFiles.clear();
            }
            return model;
        });
    }
    saveModelToCloud(model, location) {
        return __awaiter(this, void 0, void 0, function* () {
            if (model.isDirty) {
                model = yield this.saveModelToLocal(model, location);
            }
            if (model.isCloudBacked) {
                return model;
            }
            model.revision = model.revision ? model.revision + 1 : 1;
            model.isCloudBacked = true;
            const json = model.toJSON();
            try {
                yield this.cloud.put(location.key, JSON.stringify(json), location.toStorageConfig());
                localForage.setItem(location.id, json).then();
                this.usersLatestFiles.clear();
                return model;
            }
            catch (error) {
                model.revision--;
                model.isCloudBacked = false;
                throw this.translateError(error);
            }
        });
    }
    refreshLatestFiles() {
        return __awaiter(this, void 0, void 0, function* () {
            const files = new Map();
            if (!this.isLocalUser && this.syncSvc.isOnline()) {
                const list = yield this.cloudList(new StorageLocation(""));
                for (const item of list) {
                    const existing = files.get(item.id);
                    if (!existing || existing.revision < item.revision) {
                        files.set(item.id, item);
                    }
                }
            }
            console.debug("refreshLatestFiles", Array.from(files.values()));
            return files;
        });
    }
    translateError(error) {
        let errMsg;
        const status = error.statusCode || error.status || error.code || 0;
        if (error.name) {
            errMsg = error.name + ': ' + error.message;
        }
        else {
            errMsg = error.message ? error.message : error.toString();
        }
        const ex = new PersistenceException(status, errMsg, error);
        console.error(ex.toString(), error);
        return ex;
    }
}
